<template>
  <div class="admin-panel task-page task-page--users">

    <!-- TOP BAR -->
    <admin-header
      variant="full"
      :show-timer="false"
      :crumbs="[{ label: 'User Management' }]"
      :back-to="{ name: 'Admin-Panel', params: { lang: $route.params.lang } }"
      @open-logout="openLogoutModal"
    />

    <main class="ap-container ap-fade-in">

      <div class="tp-hero">
        <div class="tp-hero-icon">
          <fa-icon :icon="['fas', 'users-cog']" />
        </div>
        <div>
          <h1 class="tp-hero-title">User Management</h1>
          <p class="tp-hero-sub">Manage admin and staff accounts.</p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="um-tabs">
        <app-button
            team="staff"
            :variant="activeTab === 'staff' ? 'primary' : 'ghost'"
            size="md"
            :icon="['fas', 'users']"
            @click="activeTab = 'staff'"
          >Staff Accounts</app-button>
        <app-button
            team="admin"
            :variant="activeTab === 'admins' ? 'primary' : 'ghost'"
            size="md"
            :icon="['fas', 'user-shield']"
            @click="activeTab = 'admins'"
          >Admin Accounts</app-button>
      </div>

      <!-- ── ADMIN TAB ─────────────────────────────────────────── -->
      <section v-if="activeTab === 'admins' && isSuperAdmin" class="um-section">
        <div class="um-section-head">
          <h2>Admin Accounts</h2>
          <app-button
            team="admin"
            variant="primary"
            size="md"
            :icon="['fas', 'plus']"
            @click="openAdminForm"
          >Add Admin</app-button>
        </div>

        <div v-if="admins.length === 0" class="um-empty">No admin accounts yet.</div>
        <table v-else class="um-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Status</th>
              <th>Created By</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="adm in admins" :key="adm.id">
              <td><fa-icon :icon="['fas', 'user-tie']" class="um-icon" /> {{ adm.username }}</td>
              <td><app-badge :status="adm.status"><span>{{ adm.status }}</span></app-badge></td>
              <td>{{ adm.created_by }}</td>
              <td>{{ formatDate(adm.created_at) }}</td>
              <td>
                <button class="ap-btn ap-btn-danger um-action-btn" @click="confirmDeleteAdmin(adm)">
                  <fa-icon :icon="['fas', 'trash']" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- ── STAFF TAB ─────────────────────────────────────────── -->
      <section v-if="activeTab === 'staff'" class="um-section">
        <div class="um-section-head">
          <h2>Staff Accounts</h2>
          <app-button
            team="staff"
            variant="primary"
            size="md"
            :icon="['fas', 'plus']"
            @click="openStaffForm"
          >Add Staff</app-button>
        </div>

        <div v-if="staffList.length === 0" class="um-empty">No staff accounts yet.</div>
        <table v-else class="um-table">
          <thead>
            <tr>
              <th>Full Name</th>
              <th>Username</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Tasks</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="s in staffList"
              :key="s.id"
              class="um-staff-row"
              @click="goToStaffPanel(s)"
            >
              <td>{{ s.full_name }}</td>
              <td><code>{{ s.username }}</code></td>
              <td>{{ s.phone_number || '—' }}</td>
              <td>{{ s.email || '—' }}</td>
              <td>
                <span v-if="s.task_summary && s.task_summary.total > 0" class="um-task-chips">
                  <span class="um-task-chip um-task-chip--total">{{ s.task_summary.total }}</span>
                  <app-badge v-if="s.task_summary.in_progress > 0" size="sm" status="inprogress" :caps="false"><span>{{ s.task_summary.in_progress }} in progress</span></app-badge>
                  <app-badge v-if="s.task_summary.pending > 0" size="sm" status="pending" :caps="false"><span>{{ s.task_summary.pending }} pending</span></app-badge>
                  <app-badge v-if="s.task_summary.closed > 0" size="sm" status="close" :caps="false"><span>{{ s.task_summary.pending }} closed</span></app-badge>
                </span>
                <span v-else class="um-task-none">—</span>
              </td>
              <td><app-badge :status="s.status"><span>{{ s.status }}</span></app-badge></td>
              <td class="um-actions" @click.stop>
                <button class="ap-btn ap-btn-ghost um-action-btn" @click="openEditStaff(s)">
                  <fa-icon :icon="['fas', 'pen']" />
                </button>
                <button class="ap-btn ap-btn-danger um-action-btn" @click="confirmDeleteStaff(s)">
                  <fa-icon :icon="['fas', 'trash']" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

    </main>

    <!-- ── MODAL: CREATE ADMIN ───────────────────────────────── -->
    <div v-if="showAdminForm" class="um-modal-overlay" @click.self="closeAdminForm">
      <div class="um-modal">
        <div class="um-modal-header">
          <h3>Add Admin Account</h3>
          <button class="um-modal-close" @click="closeAdminForm"><fa-icon :icon="['fas', 'times']" /></button>
        </div>
        <form @submit.prevent="submitAdmin" class="um-form" novalidate autocomplete="off">
          <div>
            <text-input
              v-model="adminForm.username"
              label="Username"
              :required="true"
              placeholder="e.g. johnadmin"
              :icon="['fas', 'user']"
              autocomplete="off"
            />
            <span v-if="adminErrors.username" class="ap-err um-field-err"><fa-icon :icon="['fas', 'exclamation-circle']" /> {{ adminErrors.username }}</span>
          </div>
          <div>
            <password-input
              v-model="adminForm.password"
              label="Password"
              :required="true"
              :min-length="6"
              placeholder="Min. 6 characters"
              input-id="admin-pw"
            />
            <span v-if="adminErrors.password" class="ap-err um-field-err"><fa-icon :icon="['fas', 'exclamation-circle']" /> {{ adminErrors.password }}</span>
          </div>
          <div class="um-form-actions um-form-actions--row">
            <app-button type="button" team="admin" variant="ghost" size="md" style="flex:1" @click="closeAdminForm">Cancel</app-button>
            <app-button type="submit" team="admin" variant="primary" size="lg" style="flex:1">Create Admin</app-button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── MODAL: CREATE STAFF ───────────────────────────────── -->
    <div v-if="showStaffForm" class="um-modal-overlay" @click.self="closeStaffForm">
      <div class="um-modal">
        <div class="um-modal-header">
          <h3>Add Staff Account</h3>
          <button class="um-modal-close" @click="closeStaffForm"><fa-icon :icon="['fas', 'times']" /></button>
        </div>
        <div class="um-staff-create-note">
          <fa-icon :icon="['fas', 'info-circle']" />
          Staff will fill in their own profile details (name, IC, phone, bank) after their first login.
        </div>
        <form @submit.prevent="submitStaff" class="um-form" novalidate>
          <div>
            <text-input
              v-model="staffForm.username"
              label="Username"
              :required="true"
              placeholder="e.g. johntan"
              :icon="['fas', 'user']"
              @input="formatStaffUsername"
            />
            <span v-if="staffErrors.username" class="ap-err um-field-err"><fa-icon :icon="['fas', 'exclamation-circle']" /> {{ staffErrors.username }}</span>
            <span v-if="staffForm.username" class="um-field-hint">Will be stored as: <code>{{ staffForm.username }}</code></span>
          </div>
          <div>
            <email-input
              v-model="staffForm.email"
              label="Email"
              :required="true"
              placeholder="staff@example.com"
            />
            <span v-if="staffErrors.email" class="ap-err um-field-err"><fa-icon :icon="['fas', 'exclamation-circle']" /> {{ staffErrors.email }}</span>
            <span v-if="staffPasswordPreview" class="um-field-hint">Auto password: <code>{{ staffPasswordPreview }}</code></span>
          </div>
          <div class="um-form-actions">
            <button type="submit" class="ap-btn ap-btn-primary ap-btn-lg ap-btn-block">
              Create Staff
            </button>
            <button type="button" class="ap-btn ap-btn-ghost um-cancel-btn" @click="closeStaffForm">Cancel</button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── MODAL: EDIT STAFF ─────────────────────────────────── -->
    <div v-if="showEditStaff" class="um-modal-overlay" @click.self="closeEditStaff">
      <div class="um-modal um-modal--wide">
        <div class="um-modal-header">
          <h3>Edit Staff — {{ editStaffData.full_name }}</h3>
          <button class="um-modal-close" @click="closeEditStaff"><fa-icon :icon="['fas', 'times']" /></button>
        </div>
        <div class="um-info-row">
          <span><strong>Username:</strong> <code>{{ editStaffData.username }}</code></span>
          <span><strong>IC:</strong> {{ editStaffData.ic_number }}</span>
        </div>
        <form @submit.prevent="submitEditStaff" class="um-form" novalidate>
          <div class="um-form-row">
            <phone-input v-model="editStaffForm.phone" label="Phone Number" />
            <div>
              <email-input
                v-model="editStaffForm.email"
                label="Email"
                placeholder="email@example.com"
              />
              <span v-if="editStaffErrors.email" class="ap-err um-field-err"><fa-icon :icon="['fas', 'exclamation-circle']" /> {{ editStaffErrors.email }}</span>
            </div>
          </div>
          <div class="um-form-row">
            <div class="um-bank-select">
              <select-input
                v-model="editStaffForm.bank_name"
                label="Bank Name"
                :options="bankOptions"
                placeholder="Select Bank"
                :icon="['fas', 'landmark']"
              />
            </div>
            <div>
              <text-input
                v-model="editStaffForm.bank_account"
                label="Bank Account Number"
                placeholder="e.g. 123456789"
                :icon="['fas', 'hashtag']"
              />
              <span v-if="editStaffErrors.bank_account" class="ap-err um-field-err"><fa-icon :icon="['fas', 'exclamation-circle']" /> {{ editStaffErrors.bank_account }}</span>
            </div>
          </div>
          <select-input
            v-model="editStaffForm.status"
            label="Status"
            :options="statusOptions"
          />
          <div class="um-form-actions">
            <button type="submit" class="ap-btn ap-btn-primary ap-btn-lg ap-btn-block">
              Save Changes
            </button>
            <button type="button" class="ap-btn ap-btn-ghost um-cancel-btn" @click="closeEditStaff">Cancel</button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── MODAL: CREATED STAFF CREDENTIALS ─────────────────── -->
    <div v-if="createdStaff" class="um-modal-overlay">
      <div class="um-modal">
        <div class="um-modal-header">
          <h3>Staff Account Created</h3>
        </div>
        <div class="um-creds-box">
          <p>Account <strong>{{ createdStaff.username }}</strong> has been created{{ createdStaff.email_sent ? ' and credentials emailed to the staff' : '' }}.</p>
          <div class="um-cred-row"><span>Username</span><code>{{ createdStaff.username }}</code></div>
          <div class="um-cred-row"><span>Password</span><code>{{ maskPassword(createdStaff.password) }}</code></div>
          <p class="um-cred-note">Please share these credentials securely. The password cannot be recovered later.</p>
        </div>
        <div class="um-creds-footer">
          <button class="ap-btn ap-btn-primary ap-btn-lg ap-btn-block" @click="createdStaff = null">Done</button>
        </div>
      </div>
    </div>

    <!-- ── CONFIRM DELETE ────────────────────────────────────── -->
    <confirm-modal
      :show="!!deleteTarget"
      :title="deleteTarget && deleteTarget.type === 'admin' ? 'Remove Admin Account' : 'Remove Staff Account'"
      confirm-label="Remove"
      :icon="['fas', 'trash']"
      @cancel="deleteTarget = null"
      @confirm="executeDelete"
    >
      {{ deleteTarget ? 'Are you sure you want to remove ' + (deleteTarget.username || deleteTarget.full_name) + '? This cannot be undone.' : '' }}
    </confirm-modal>

    <!-- Alert -->
    <alert-modal :show="showAlert" :message="alertMessage" :status="alertStatus" @close="showAlert = false" />

    <!-- Logout confirm -->
    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="showLogoutModal = false"
      @confirm="doLogout"
    >Are you sure you want to logout?</confirm-modal>

  </div>
</template>

<script>
import AdminHeader from '@/components/admin/AdminHeader.vue'
import { apiFetch } from '@/services/api'
import { withLoader } from '@/services/loader'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import AlertModal from '@/components/shared/AlertModal.vue'
import AppButton from '@/components/shared/AppButton.vue'
import AppBadge from '../../components/shared/AppBadge.vue'
import TextInput from '@/components/form/TextInput.vue'
import PasswordInput from '@/components/form/PasswordInput.vue'
import PhoneInput from '@/components/form/PhoneInput.vue'
import EmailInput from '@/components/form/EmailInput.vue'
import SelectInput from '@/components/form/SelectInput.vue'

const MALAYSIAN_BANKS = [
  'Maybank', 'CIMB Bank', 'Public Bank Berhad', 'RHB Bank', 'Hong Leong Bank',
  'AmBank', 'UOB Malaysia', 'OCBC Bank Malaysia', 'HSBC Bank Malaysia',
  'Standard Chartered Bank Malaysia', 'Alliance Bank Malaysia', 'Affin Bank',
  'Bank Islam Malaysia', 'Bank Muamalat Malaysia', 'Bank Rakyat',
  'MBSB Bank', 'Agrobank', 'BSN (Bank Simpanan Nasional)', 'Bank of China (Malaysia)',
  'EXIM Bank'
]

export default {
  name: 'admin-user-management',
  components: { AdminHeader, ConfirmModal, AlertModal, AppButton, AppBadge, TextInput, PasswordInput, PhoneInput, EmailInput, SelectInput },
  data () {
    return {
      activeTab: 'staff',

      admins: [],
      staffList: [],

      showAdminForm: false,
      adminForm: { username: '', password: '' },
      adminErrors: {},

      showStaffForm: false,
      staffForm: { username: '', email: '' },
      staffErrors: {},

      showEditStaff: false,
      editStaffData: {},
      editStaffForm: { phone: '', email: '', bank_name: '', bank_account: '', status: 'active' },
      editStaffErrors: {},

      createdStaff: null,
      deleteTarget: null,

      showAlert: false,
      alertMessage: '',
      alertStatus: 'success',

      showLogoutModal: false,

      bankOptions: MALAYSIAN_BANKS.map(b => ({ value: b, label: b })),
      statusOptions: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    }
  },
  computed: {
    isSuperAdmin () {
      return localStorage.getItem('adminRole') === 'super_admin'
    },
    staffPasswordPreview () {
      const email = this.staffForm.email.trim()
      if (!email || !email.includes('@')) return ''
      return 'pw_' + email.split('@')[0]
    }
  },
  created () {
    if (!this.isSuperAdmin) {
      this.$router.replace({ name: 'Admin-Panel', params: { lang: this.$route.params.lang } })
      return
    }
    this.activeTab = 'staff'
    this.fetchAll()
  },
  methods: {
    async fetchAll () {
      if (this.isSuperAdmin) this.fetchAdmins()
      this.fetchStaff()
    },
    async fetchAdmins () {
      await withLoader(async () => {
        const data = await apiFetch('/api/admin/user-management/list-admins.php')
        if (data.success) this.admins = data.admins
      })
    },
    async fetchStaff () {
      await withLoader(async () => {
        const data = await apiFetch('/api/admin/user-management/list-staff.php')
        if (data.success) this.staffList = data.staff
      })
    },

    openAdminForm () {
      this.adminForm = { username: '', password: '' }
      this.adminErrors = {}
      this.showAdminForm = true
    },
    closeAdminForm () { this.showAdminForm = false },
    async submitAdmin () {
      this.adminErrors = {}
      if (!this.adminForm.username) this.adminErrors.username = 'Username is required'
      else if (this.adminForm.username.toLowerCase() === 'superadmin') this.adminErrors.username = '"superadmin" is a reserved username'
      else if (this.adminForm.username.length < 3) this.adminErrors.username = 'At least 3 characters'
      else if (!/^[a-zA-Z0-9._-]+$/.test(this.adminForm.username)) this.adminErrors.username = 'Letters, numbers, dots, hyphens, underscores only'
      if (!this.adminForm.password) this.adminErrors.password = 'Password is required'
      else if (this.adminForm.password.length < 6) this.adminErrors.password = 'At least 6 characters'
      if (Object.keys(this.adminErrors).length) return

      await withLoader(async () => {
        try {
          const data = await apiFetch('/api/admin/user-management/create-admin.php', { method: 'POST', body: JSON.stringify(this.adminForm) })
          if (data.success) {
            this.closeAdminForm()
            this.fetchAdmins()
            this.showAlertMsg('Admin account created successfully', 'success')
          } else {
            if (data.errors) this.adminErrors = data.errors
            else this.showAlertMsg(data.message || 'Failed to create admin', 'error')
          }
        } catch (err) {
          this.showAlertMsg(err.message || 'Failed to create admin', 'error')
        }
      })
    },

    openStaffForm () {
      this.staffForm = { username: '', email: '' }
      this.staffErrors = {}
      this.showStaffForm = true
    },
    closeStaffForm () { this.showStaffForm = false },
    formatStaffUsername () {
      this.staffForm.username = this.staffForm.username.toLowerCase().replace(/\s+/g, '')
    },
    async submitStaff () {
      this.staffErrors = {}
      this.staffForm.username = this.staffForm.username.toLowerCase().replace(/\s+/g, '')
      if (!this.staffForm.username) {
        this.staffErrors.username = 'Username is required'
      } else if (!/^[a-z0-9._-]+$/.test(this.staffForm.username)) {
        this.staffErrors.username = 'Letters, numbers, dots, hyphens, underscores only'
      }
      if (!this.staffForm.email) {
        this.staffErrors.email = 'Email is required'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.staffForm.email)) {
        this.staffErrors.email = 'Invalid email address'
      }
      if (Object.keys(this.staffErrors).length) return

      await withLoader(async () => {
        try {
          const data = await apiFetch('/api/admin/user-management/create-staff.php', { method: 'POST', body: JSON.stringify(this.staffForm) })
          if (data.success) {
            this.closeStaffForm()
            this.fetchStaff()
            this.createdStaff = { username: data.username, password: data.password, email_sent: data.email_sent }
          } else {
            if (data.errors) {
              this.staffErrors = data.errors
              this.showAlertMsg(Object.values(data.errors)[0] || data.message || 'Failed to create staff', 'error')
            } else {
              this.showAlertMsg(data.message || 'Failed to create staff', 'error')
            }
          }
        } catch (err) {
          this.showAlertMsg(err.message || 'Failed to create staff', 'error')
        }
      })
    },

    openEditStaff (s) {
      this.editStaffData = s
      this.editStaffForm = {
        phone: s.phone_number || '',
        email: s.email || '',
        bank_name: s.bank_name || '',
        bank_account: s.bank_account || '',
        status: s.status
      }
      this.editStaffErrors = {}
      this.showEditStaff = true
    },
    closeEditStaff () { this.showEditStaff = false },
    async submitEditStaff () {
      this.editStaffErrors = {}
      if (this.editStaffForm.bank_account && !/^\d+$/.test(this.editStaffForm.bank_account)) this.editStaffErrors.bank_account = 'Numbers only'
      if (Object.keys(this.editStaffErrors).length) return

      await withLoader(async () => {
        try {
          const payload = { id: this.editStaffData.id, ...this.editStaffForm }
          const data = await apiFetch('/api/admin/user-management/update-staff.php', { method: 'PUT', body: JSON.stringify(payload) })
          if (data.success) {
            this.closeEditStaff()
            this.fetchStaff()
            this.showAlertMsg('Staff record updated', 'success')
          } else {
            if (data.errors) this.editStaffErrors = data.errors
            else this.showAlertMsg(data.message || 'Failed to update', 'error')
          }
        } catch (err) {
          this.showAlertMsg(err.message || 'Failed to update', 'error')
        }
      })
    },

    confirmDeleteAdmin (adm) { this.deleteTarget = { ...adm, type: 'admin' } },
    confirmDeleteStaff (s) { this.deleteTarget = { ...s, type: 'staff' } },
    async executeDelete () {
      if (!this.deleteTarget) return
      const type = this.deleteTarget.type
      const url = type === 'admin'
        ? '/api/admin/user-management/delete-admin.php'
        : '/api/admin/user-management/delete-staff.php'
      const body = JSON.stringify({ id: this.deleteTarget.id })
      this.deleteTarget = null
      await withLoader(async () => {
        try {
          const data = await apiFetch(url, { method: 'DELETE', body })
          if (data.success) {
            if (type === 'admin') this.fetchAdmins()
            else this.fetchStaff()
            this.showAlertMsg('Account removed', 'success')
          } else {
            this.showAlertMsg(data.message || 'Failed to remove account', 'error')
          }
        } catch {
          this.showAlertMsg('Network error', 'error')
        }
      })
    },

    openLogoutModal () { this.showLogoutModal = true },
    async doLogout () {
      const refreshToken = localStorage.getItem('adminRefreshToken')
      if (refreshToken) {
        await apiFetch('/api/admin/logout.php', { method: 'POST', body: JSON.stringify({ refresh_token: refreshToken }) }).catch(() => {})
      }
      localStorage.removeItem('adminToken')
      localStorage.removeItem('adminRefreshToken')
      localStorage.removeItem('adminRole')
      this.$router.push({ name: 'Admin-Login', params: { lang: this.$route.params.lang } })
    },

    goBack () {
      this.$router.push({ name: 'Admin-Panel', params: { lang: this.$route.params.lang } })
    },

    goToStaffPanel (staff) {
      this.$router.push({ name: 'Admin-Staff-Panel', params: { lang: this.$route.params.lang, id: staff.id } })
    },

    showAlertMsg (msg, status) {
      this.alertMessage = msg
      this.alertStatus = status
      this.showAlert = true
    },

    formatDate (dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    maskPassword (pw) {
      if (!pw || pw.length <= 2) return pw
      return pw[0] + '*'.repeat(pw.length - 2) + pw[pw.length - 1]
    }
  }
}
</script>

<style scoped>
.um-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
}

.um-tab {
  padding: 10px 20px;
  border: 1.5px solid #dde3ee;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all .18s;
}

.um-tab--active {
  background: #1a3c6e;
  border-color: #1a3c6e;
  color: #fff;
}

.um-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.um-section-head h2 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.um-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.um-table th {
  text-align: left;
  padding: 10px 12px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .04em;
  border-bottom: 1.5px solid #e2e8f0;
}

.um-table td {
  padding: 12px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}

.um-table code {
  padding: 4px 12px;
  border-radius: 4px;
  background-color: #e5e7eb;
  color: #374151;
}

.um-table tbody tr:hover { background: #f8fafc; }

.um-badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.um-badge--green { background: #dcfce7; color: #15803d; border: 1px solid #15803d }
.um-badge--grey  { background: #f1f5f9; color: #64748b; }

.um-actions { display: flex; gap: 6px; }
.um-icon    { color: #94a3b8; margin-right: 6px; font-size: 12px; }

.um-loading,
.um-empty {
  text-align: center;
  padding: 40px;
  color: #94a3b8;
  font-size: 14px;
}

/* Modals */
.um-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15,23,42,.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.um-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 24px 60px rgba(0,0,0,.18);
}

.um-modal--wide { max-width: 700px; }

.um-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
}

.um-modal-header h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.um-modal-close {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
}

.um-form { padding: 8px 24px 24px; }
.um-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0 16px; }

.um-field-err {
  display: block;
  margin-top: -8px;
  margin-bottom: 4px;
  font-size: 12px;
}

.um-form-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}
.um-form-actions--row {
  flex-direction: row;
  gap: 16px;
}

.um-cancel-btn {
  align-self: center;
  font-size: 13px;
}

.um-action-btn {
  padding: 5px 10px;
  font-size: 12px;
}

.um-info-row {
  display: flex;
  gap: 24px;
  padding: 12px 24px;
  background: #f8fafc;
  font-size: 13px;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
}

.um-creds-box { padding: 16px 24px; }

.um-cred-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 14px;
}

.um-cred-row span { color: #64748b; font-weight: 500; }
.um-cred-row code { font-size: 15px; font-weight: 700; color: #1a3c6e; letter-spacing: .03em; }
.um-cred-note { font-size: 12px; color: #94a3b8; margin-top: 12px; }

.ap-crumb-link { cursor: pointer; color: #64748b; }
.ap-crumb-link:hover { color: #1a3c6e; }

@media (max-width: 600px) {
  .um-form-row { grid-template-columns: 1fr; }
}

.um-bank-select ::v-deep .ap-si-menu {
  max-height: 200px;
  overflow-y: auto;
}

.um-staff-row { cursor: pointer; }
.um-staff-row:hover { background: #eff6ff !important; }

.um-task-chips { display: flex; gap: 4px; flex-wrap: wrap; }
.um-task-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}
.um-task-chip--total    { background: #e2e8f0; color: #475569; }
.um-task-chip--active   { background: #dbeafe; color: #1d4ed8; }
.um-task-chip--pending  { background: #fef9c3; color: #854d0e; }
.um-task-none { color: #cbd5e1; }

.um-creds-footer {
  padding: 0 24px 24px;
}

.um-staff-create-note {
  margin: 12px 24px 0;
  padding: 10px 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 13px;
  color: #1d4ed8;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.um-field-hint {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}
.um-field-hint code {
  font-size: 12px;
  color: #1a3c6e;
  font-weight: 600;
}

</style>
